import React, { useContext } from "react";
import LanguageContext from "../../utils/Contexts/LanguageContext";
import moment from "moment";
import { useNavigate } from "react-router-dom";

const ContractTable = ({
  data,
  setData,
  columnsVisibility,
  setColumnSorts,
  columnSorts,
}) => {
  const { t } = useContext(LanguageContext);
  const navigate = useNavigate();

  const setColumnSort = (columnIndex) => {
    setColumnSorts((prevSorts) => ({
      ...prevSorts,
      [columnIndex]:
        prevSorts[columnIndex] === "asc"
          ? "desc"
          : prevSorts[columnIndex] === "desc"
          ? null
          : "asc",
    }));
  };

  const placementItems = {
    1: t("Guest Post"),
    2: t("Link Insert"),
    3: t("Niche Edit"),
  };

  const linkTypesItems = {
    1: t("Do Follow"),
    2: t("No Follow"),
    3: t("Sponsored"),
  };

  const moneyBackPolicyType = {
    0: t("Not Provided"),
    1: `5 ${t("Days")}`,
    2: `10 ${t("Days")}`,
    3: `15 ${t("Days")}`,
    4: `20 ${t("Days")}`,
    5: `25 ${t("Days")}`,
    6: `30 ${t("Days")}`,
    7: `40 ${t("Days")}`,
    8: `50 ${t("Days")}`,
    9: `60 ${t("Days")}`,
    10: `70 ${t("Days")}`,
    11: `80 ${t("Days")}`,
    12: `90 ${t("Days")}`,
  };

  const linkReplacementPolicyType = {
    0: t("Not Provided"),
    1: `1 ${t("Month")}`,
    2: `2 ${t("Months")}`,
    3: `3 ${t("Months")}`,
    4: `4 ${t("Months")}`,
    5: `5 ${t("Months")}`,
    6: `6 ${t("Months")}`,
    7: `7 ${t("Months")}`,
    8: `8 ${t("Months")}`,
    9: `9 ${t("Months")}`,
    10: `10 ${t("Months")}`,
    11: `11 ${t("Months")}`,
    12: `12 ${t("Months")}`,
  };

  const contractStatusType = {
    0: {
      title: t("Created"),
      class: "text-gray bg-soft-secondary",
      contractClass: "text-gray",
    },
    1: {
      title: t("Waiting Acceptance"),
      class: "text-warning bg-softer-warning",
      contractClass: "text-warning",
    },
    2: {
      title: t("Declined"),
      class: "text-orange bg-text-orange",
      contractClass: "text-orange",
    },
    3: {
      title: t("Canceled"),
      class: "text-danger bg-soft-danger",
      contractClass: "text-danger",
    },
    4: {
      title: t("In Progress"),
      class: "text-primary bg-soft-primary",
      contractClass: "text-primary",
    },
    5: {
      title: t("Canceled"),
      class: "text-danger bg-soft-danger",
      contractClass: "text-danger",
    },
    6: {
      title: t("Approved"),
      class: "text-info bg-soft-primary",
      contractClass: "text-info",
    },
    7: {
      title: t("Rejected"),
      class: "text-orange bg-text-orange",
      contractClass: "text-orange",
    },
    8: {
      title: t("Declined"),
      class: "text-orange bg-text-orange",
      contractClass: "text-orange",
    },
    9: {
      title: t("Completed"),
      class: "text-success bg-soft-success",
      contractClass: "text-success",
    },
    10: {
      title: t("Deleted"),
      class: "text-muted bg-soft-danger",
      contractClass: "text-muted",
    },
    11: {
      title: t("Canceled"),
      class: "text-danger bg-soft-danger",
      contractClass: "text-danger",
    },
    12: {
      title: t("Canceled"),
      class: "text-danger bg-soft-danger",
      contractClass: "text-danger",
    },
  };

  const linkStatusType = {
    true: {
      title: t("Successful"),
      class: "text-success bg-soft-success",
    },
    false: {
      title: t("Failed"),
      class: "text-danger bg-soft-danger",
    },
    null: {
      title: "-",
      class: "custom-text-gray",
    },
  };

  const columnsHeader = [
    { key: "contract", label: t("Contract") },
    { key: "created", label: t("Created") },
    { key: "placement", label: t("Placement") },
    { key: "link_type", label: t("Link Type") },
    { key: "money_back", label: t("Money Back") },
    { key: "link_replacement", label: t("Link Replacement") },
    { key: "status", label: t("Status") },
    { key: "price", label: t("Price") },
    { key: "links_check", label: t("Links Check") },
  ];

  const linkTypesItemsStyles = {
    [t("Do Follow")]: "text-primary",
    [t("No Follow")]: "",
    [t("Sponsored")]: "custom-text-gray",
  };

  const formatStatus = (status, moneyback_policy_ends) => {
    if (moneyback_policy_ends === null || moneyback_policy_ends <= 0) {
      return t("(expired)");
    }
    if (status === 9) {
      const now = moment(); // Local time
      const deviceOffsetMinutes = new Date().getTimezoneOffset(); // minutes
      const moneybackUTC = moment.utc(moneyback_policy_ends); // Ensure it's treated as UTC
      const endsAt = moneybackUTC.subtract(deviceOffsetMinutes, "minutes"); // Convert to device local time

      const diff = endsAt.diff(now);

      if (diff <= 0) {
        return t("(expired)");
      }

      const duration = moment.duration(diff);
      const days = Math.floor(duration.asDays());
      const hours = duration.hours();
      const minutes = duration.minutes();
      const seconds = duration.seconds();
      const totalDays = days;
      const totalHours = days * 24 + hours;
      const totalMinutes = totalHours * 60 + minutes;
      const totalSeconds = totalMinutes * 60 + seconds;

      if (totalDays > 7) {
        return `(${days}d ${t("left")})`;
      } else if (totalDays >= 1) {
        return `(${Math.floor(totalHours)}h ${t("left")})`;
      } else if (totalHours >= 1) {
        return `(${Math.floor(totalMinutes)}m ${t("left")})`;
      } else if (totalMinutes >= 1) {
        return `(${minutes}m ${t("left")})`;
      } else if (totalSeconds > 0) {
        return `(${seconds}s ${t("left")})`;
      } else {
        return null;
      }
    }
  };

  const columnHeaderValue = {
    contract: {
      value: (item) => item?.code,
      className: (item) =>
        `${
          contractStatusType[item?.status]?.contractClass
        } text-decoration-underline cursor-pointer`,
      style: {
        fontSize: "16px",
        fontWeight: "400",
      },
    },
    created: {
      value: (item) => localizeDate(item?.created),
      className: (item) => "custom-text-gray",
    },
    placement: {
      value: (item) => placementItems[item?.type],
      className: (item) => "custom-text-gray",
    },
    link_type: {
      value: (item) => `${item?.links} · ${linkTypesItems[item?.rel]}`,
      className: (item) => linkTypesItemsStyles[linkTypesItems[item?.rel]],
    },
    money_back: {
      value: (item) => (
        <>
          <span className="custom-text-gray">
            {item?.money_back_policy > 0 &&
              moneyBackPolicyType[item?.money_back_policy]}
          </span>{" "}
          <span>{formatStatus(item?.status, item?.moneyback_policy_ends)}</span>
        </>
      ),
    },
    link_replacement: {
      value: (item) => (
        <>
          <span className="custom-text-gray">
            {linkReplacementPolicyType[item?.link_replacement_policy]}
          </span>{" "}
          <span>
            {formatStatus(item?.status, item?.link_replacement_policy_ends)}
          </span>
        </>
      ),
    },
    status: {
      value: (item) => contractStatusType[item?.status]?.title,
      className: (item) =>
        `${contractStatusType[item?.status]?.class} py-1 rounded-1`,
      style: { width: "157px", textAlign: "center" },
    },
    price: {
      value: (item) => `$${item?.price}`,
      className: () => "fw-bold text-white",
    },
    links_check: {
      value: (item) =>
        linkStatusType[item?.links_check] ? (
          <span className="text-center w-100 d-block">
            {linkStatusType[item?.links_check]?.title}
          </span>
        ) : (
          "─"
        ),
      className: (item) =>
        `${
          linkStatusType[item?.links_check]
            ? linkStatusType[item?.links_check]?.class
            : "text-secondary"
        } py-1`,
      style: { width: "95px", borderRadius: "5px" },
    },
  };

  const navigateToOpenDetails = (uuid) => {
    navigate(`/contracts/${uuid}`);
  };

  const localizeDate = (date) => {
    const localizeDateText = moment(date).format("MMM DD, YYYY");
    const mappings = {
      Jan: t("Jan"),
      Feb: t("Feb"),
      Mar: t("Mar"),
      Apr: t("Apr"),
      May: t("May"),
      Jun: t("Jun"),
      Jul: t("Jul"),
      Aug: t("Aug"),
      Sep: t("Sep"),
      Oct: t("Oct"),
      Nov: t("Nov"),
      Dec: t("Dec"),
    };

    // Replace text units with localized values
    return localizeDateText
      .split(" ")
      .map((word) => mappings[word] || word)
      .join(" ");
  };

  return (
    <div className="table-responsive datatable-custom">
      <table
        id="datatable"
        className="table table-lg table-borderless table-thead-bordered table-nowrap table-align-middle card-table"
      >
        <thead className="thead-light">
          <tr>
            {columnsHeader.map(({ key, label }, index) =>
              columnsVisibility[key] ? (
                <th
                  key={key}
                  className={`sorting${
                    columnSorts[index] ? ` sorting_${columnSorts[index]}` : ""
                  }`}
                  onClick={() => setColumnSort(index)}
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "var(--gray)",
                  }}
                >
                  {t(label)}
                </th>
              ) : null
            )}
            <th className="table-column-ps-0"></th>
          </tr>
        </thead>
        <tbody
          id="nav-one-eg1"
          aria-labelledby="nav-one-eg1-tab"
          className="table-row-main"
        >
          {!!data?.length ? (
            data.map((item, index) => (
              <>
                <tr key={index}>
                  {Object.entries(columnsVisibility)?.map(
                    ([key, isVisible]) =>
                      isVisible && (
                        <td
                          key={key}
                          onClick={() =>
                            key === "contract"
                              ? navigateToOpenDetails(item?.uuid)
                              : {}
                          }
                        >
                          <span
                            style={{
                              ...columnHeaderValue[key]?.style,
                              display: "inline-block",
                            }}
                            className={`${columnHeaderValue[key]?.className?.(
                              item
                            )}`}
                          >
                            {columnHeaderValue[key]?.value(item)}
                          </span>
                        </td>
                      )
                  )}
                  <td className="p-1">
                    <div className="dropdowm">
                      <button
                        type="button"
                        className="btn btn-ghost-secondary btn-icon btn-sm rounded-circle"
                        id="teamsDropdown2"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                      >
                        <i className="bi-three-dots-vertical"></i>
                      </button>

                      <div
                        className="dropdown-menu dropdown-menu-sm dropdown-menu-end"
                        aria-labelledby="teamsDropdown2"
                      >
                        <button
                          className="dropdown-item"
                          // data-bs-toggle="modal"
                          // data-bs-target="#modal-sm-add-remove-to-lists"
                          onClick={() => navigateToOpenDetails(item?.uuid)}
                        >
                          <i className="bi bi-journal"></i>
                          {"  "}
                          {t("Open")}
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              </>
            ))
          ) : (
            <>
              <tr>
                <td colSpan="100%">
                  <div className="text-center p-4 no-data-wrapper">
                    <img
                      className="mb-3"
                      src="https://static.articlesfactory.ai/app/svg/illustrations-light/oc-error.svg"
                      alt=""
                      style={{ width: "10rem" }}
                      data-hs-theme-appearance="dark"
                    />
                    <img
                      className="mb-3"
                      src="https://static.articlesfactory.ai/app/svg/illustrations/oc-error.svg"
                      alt=""
                      style={{ width: "10rem" }}
                      data-hs-theme-appearance="default"
                    />
                    <p className="mb-0">
                      {!data ? t("Loading data") : t("No data to show")}
                    </p>
                  </div>
                </td>
              </tr>
            </>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default ContractTable;
